/**
 * Migration History - Persistent record of applied migrations.
 *
 * Stored in localStorage under HISTORY_KEY.
 * Provides:
 *   - Idempotency: prevent re-running a completed migration
 *   - Crash recovery: detect in-progress migrations interrupted by page crash
 *   - Audit trail: permanent record of all schema evolution events
 *
 * The history record is intentionally small - one entry per unique
 * (storageKey × fromVersion × toVersion) tuple.
 */

import { logger } from "@/system/logger";
import type { MigrationHistoryRecord, MigrationStatus } from "./types";

const HISTORY_KEY    = "nuvra-migration-history";
const MAX_RECORDS    = 500; // evict oldest complete/failed when exceeded
const STALE_CRASH_MS = 86_400_000; // 24 h - in-progress older than this = stale

/* =========================================================
   PERSISTENCE HELPERS
   ========================================================= */

type HistoryStore = Record<string, MigrationHistoryRecord>;

function readStore(): HistoryStore {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as HistoryStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: HistoryStore): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(store));
  } catch (err) {
    logger.error("MigrationHistory: write failed", { err });
  }
}

function makeId(storageKey: string, fromVersion: number, toVersion: number): string {
  return `${storageKey}::${fromVersion}::${toVersion}`;
}

function evictOldest(store: HistoryStore): void {
  const records = Object.values(store);
  if (records.length <= MAX_RECORDS) return;

  // Evict oldest completed or failed records first (keep in-progress, they're active)
  const evictable = records
    .filter(r => r.status === "complete" || r.status === "failed" || r.status === "rolled-back")
    .sort((a, b) => (a.startedAt) - (b.startedAt));

  const toEvict = records.length - MAX_RECORDS;
  for (let i = 0; i < Math.min(toEvict, evictable.length); i++) {
    delete store[evictable[i].id];
  }
}

/* =========================================================
   MIGRATION HISTORY API
   ========================================================= */

export const MigrationHistory = {

  /**
   * Record a migration as started.
   * Must be called BEFORE the backup is written and the migration begins.
   */
  markInProgress(
    storageKey: string,
    fromVersion: number,
    toVersion: number,
    backupKey: string,
  ): void {
    const store  = readStore();
    const id     = makeId(storageKey, fromVersion, toVersion);
    store[id] = {
      id,
      storageKey,
      fromVersion,
      toVersion,
      status:      "in-progress",
      startedAt:   Date.now(),
      completedAt: null,
      error:       null,
      backupKey,
    };
    evictOldest(store);
    writeStore(store);
  },

  /**
   * Mark a migration as successfully completed.
   * Called after the migrated data has been saved and the backup removed.
   */
  markComplete(storageKey: string, fromVersion: number, toVersion: number): void {
    const store = readStore();
    const id    = makeId(storageKey, fromVersion, toVersion);
    if (!store[id]) return;
    store[id] = {
      ...store[id],
      status:      "complete",
      completedAt: Date.now(),
      backupKey:   null,  // backup was removed on commit
      error:       null,
    };
    writeStore(store);
  },

  /**
   * Mark a migration as failed (backup was not restored - caller rolled back separately).
   */
  markFailed(
    storageKey: string,
    fromVersion: number,
    toVersion: number,
    error: string,
  ): void {
    const store = readStore();
    const id    = makeId(storageKey, fromVersion, toVersion);
    if (!store[id]) return;
    store[id] = {
      ...store[id],
      status:      "failed",
      completedAt: Date.now(),
      error,
    };
    writeStore(store);
  },

  /**
   * Mark a migration as rolled-back (backup was restored).
   */
  markRolledBack(
    storageKey: string,
    fromVersion: number,
    toVersion: number,
    error: string,
  ): void {
    const store = readStore();
    const id    = makeId(storageKey, fromVersion, toVersion);
    if (!store[id]) return;
    store[id] = {
      ...store[id],
      status:      "rolled-back",
      completedAt: Date.now(),
      backupKey:   null,
      error,
    };
    writeStore(store);
  },

  /**
   * Returns true if this exact migration has already been successfully completed.
   * Used for idempotency checks before re-running.
   */
  hasCompleted(storageKey: string, fromVersion: number, toVersion: number): boolean {
    const store  = readStore();
    const record = store[makeId(storageKey, fromVersion, toVersion)];
    return record?.status === "complete";
  },

  /**
   * Returns all in-progress records - these represent migrations interrupted by
   * a page crash. The caller (initMigrationEngine) handles crash recovery.
   *
   * Records older than STALE_CRASH_MS are returned separately as stale
   * to allow the caller to decide on cleanup policy.
   */
  getPendingCrashRecovery(): {
    active: MigrationHistoryRecord[];
    stale:  MigrationHistoryRecord[];
  } {
    const store   = readStore();
    const now     = Date.now();
    const records = Object.values(store).filter(r => r.status === "in-progress");
    return {
      active: records.filter(r => now - r.startedAt <= STALE_CRASH_MS),
      stale:  records.filter(r => now - r.startedAt >  STALE_CRASH_MS),
    };
  },

  /** Get a specific record by key + version tuple (for diagnostics). */
  get(storageKey: string, fromVersion: number, toVersion: number): MigrationHistoryRecord | null {
    const store = readStore();
    return store[makeId(storageKey, fromVersion, toVersion)] ?? null;
  },

  /** All records (for diagnostics/observability). */
  getAll(): MigrationHistoryRecord[] {
    return Object.values(readStore()).sort((a, b) => b.startedAt - a.startedAt);
  },

  /** Purge all complete/failed/rolled-back records older than retentionMs. */
  prune(retentionMs = 7 * 86_400_000): void {
    const store  = readStore();
    const cutoff = Date.now() - retentionMs;
    for (const [id, record] of Object.entries(store)) {
      if (
        record.status !== "in-progress" &&
        record.startedAt < cutoff
      ) {
        delete store[id];
      }
    }
    writeStore(store);
  },
};
